import InfiniteProgress from "components/_common/InfiniteProgress";
import React, { useMemo } from "react";
import { connect, ConnectedProps } from "react-redux";
import { Dispatch } from "redux";
import reportSelectors from "redux/app/report/selectors";
import {
	isEmissionsDataEmpty,
	tranformEmissionsDataForTable,
	transformAggregateAllVesselsEmissionsDataForTable,
} from "redux/app/report/utils";
import { IStore } from "redux/store";

const emissionHeaders = [
	"Year",
	"Quarter",
	"VOC",
	"NMVOC",
	"Total Organic Emissions",
];

type OwnProps = {
	vesselId: string;
	year: string;
};
type EmissionTableProps = PropsFromRedux & OwnProps;
function EmissionsTable(props: EmissionTableProps) {
	const {
		year,
		vesselId,
		rawEmissionVesselSpecificData,
		rawEmissionsAllVesselsData,
		jobToLoadVesselTenantEmissionsReport,
		jobToGetVesselSpecificEmissionsReport,
	} = props;

	const tableRowRecords = useMemo(() => {
		if (vesselId) {
			return tranformEmissionsDataForTable(rawEmissionVesselSpecificData);
		} else {
			return transformAggregateAllVesselsEmissionsDataForTable(
				rawEmissionsAllVesselsData,
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [rawEmissionVesselSpecificData, rawEmissionsAllVesselsData, vesselId]);

	const handleExportCSV = () => {
		if (!tableRowRecords || !tableRowRecords.length) return;

		// Generate CSV content
		const rows = tableRowRecords.map((row) => {
			return [
				year,
				row.quarter,
				row.VOC.toFixed(2),
				row.NMVOC.toFixed(2),
				row.quarterTotal.toFixed(2),
			]
				.map((val) => {
					const str = String(val);
					return str.includes(",") ? `"${str}"` : str;
				})
				.join(",");
		});

		// Add headers
		const csvContent = [emissionHeaders.join(","), ...rows].join("\n");

		// Create and trigger download
		const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
		const link = document.createElement("a");
		const url = URL.createObjectURL(blob);
		link.setAttribute("href", url);
		link.setAttribute("download", `emissions_${vesselId || "all"}_${year}.csv`);
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
	};

	if (
		(!vesselId && jobToLoadVesselTenantEmissionsReport.isLoading) ||
		(vesselId && jobToGetVesselSpecificEmissionsReport.isLoading)
	) {
		return <div className="flex justify-center mt-20">Loading...</div>;
	}

	if (isEmissionsDataEmpty(tableRowRecords)) {
		return (
			<div className="flex justify-center items-center mt-20">
				No data found{vesselId ? " for this vessel" : ""}.{" "}
				{vesselId
					? "Choose another vessel."
					: "Please select a vessel or change the year."}
			</div>
		);
	}

	return (
		<div className="px-4">
			<div className="flex justify-end mb-4">
				<button
					className="p-3 px-6 py-1 mt-1 mr-1 text-base text-indigo-700 border border-indigo-600 rounded-sm outline-none ws-button-og"
					disabled={!tableRowRecords || tableRowRecords.length === 0}
					title={
						!tableRowRecords || tableRowRecords.length === 0
							? "No data to export"
							: "Export data as CSV"
					}
					onClick={handleExportCSV}
				>
					<span className="mr-3 text-sm">Export Data</span>
					<span className="mb-0.5 text-sm bp3-icon bp3-icon-download"></span>
				</button>
			</div>
			<InfiniteProgress
				isLoading={jobToLoadVesselTenantEmissionsReport.isLoading}
				isSpacedOut={false}
			/>
			<table
				id="emissions-table"
				className="w-full h-full border border-gray-400 table-auto rounded-md font-redhat-text  overflow-auto  border-collapse text-center"
			>
				<thead>
					<tr>
						{emissionHeaders.map((headerLabel) => (
							<th
								key={headerLabel}
								className="px-3 py-3 text-sm border font-medium leading-4 tracking-wide text-center text-gray-700 bg-gray-100 border-b border-gray-300"
							>
								{headerLabel}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{tableRowRecords.map((row, index) => {
						const { quarter, VOC, NMVOC, quarterTotal } = row;
						return (
							<tr key={row.quarter}>
								{index === 0 && (
									<td
										rowSpan={5}
										className="px-1 text-center py-3 text-[8px] lg:text-xs xl:text-[12px] border leading-4 tracking-wide border-b border-gray-300 font-redhat-text text-[#2D2D2D]"
									>
										{year}
									</td>
								)}
								<td className="px-1 text-center py-3 text-[8px] lg:text-xs xl:text-[12px] border leading-4 tracking-wide border-b border-gray-300 font-redhat-text text-[#2D2D2D]">
									{quarter}
								</td>
								<td className="px-1 text-center py-3 text-[8px] lg:text-xs xl:text-[12px] border leading-4 tracking-wide border-b border-gray-300 font-redhat-text text-[#2D2D2D]">
									{VOC.toFixed(2)}
								</td>
								<td className="px-1 text-center py-3 text-[8px] lg:text-xs xl:text-[12px] border leading-4 tracking-wide border-b border-gray-300 font-redhat-text text-[#2D2D2D]">
									{NMVOC.toFixed(2)}
								</td>
								<td className="px-1 text-center py-3 text-[8px] lg:text-xs xl:text-[12px] border leading-4 tracking-wide border-b border-gray-300 font-redhat-text text-[#2D2D2D]">
									{quarterTotal.toFixed(2)}
								</td>
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
}

function mapStateToProps(store: IStore, ownProps: OwnProps) {
	const reportStore = reportSelectors._getStore(store);
	const rawEmissionsAllVesselsData =
		reportSelectors.getEmissionsAllVesselsAggregateData(reportStore);
	const rawEmissionVesselSpecificData =
		reportSelectors.getEmissionsVesselSpecificAggregateData(reportStore);
	const jobToGetVesselSpecificEmissionsReport =
		reportSelectors.jobToLoadVesselSpecificEmissionsAggregate(reportStore);
	const jobToLoadVesselTenantEmissionsReport =
		reportSelectors.jobToLoadAllVesselsEmissionsAggregate(reportStore);

	return {
		rawEmissionVesselSpecificData,
		rawEmissionsAllVesselsData,
		jobToLoadVesselTenantEmissionsReport,
		jobToGetVesselSpecificEmissionsReport,
	};
}
function mapDispatchToProps(dispatch: Dispatch, ownProps: OwnProps) {
	return {};
}
const reduxConnector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof reduxConnector>;
export default reduxConnector(EmissionsTable);
